<script setup>
import { ButtonSecondaryLg } from "@easy/ui/buttons";
import { SkipForwardIcon } from "@easy/ui/icons";
import { NotificationWithVideo } from "@easy/ui/notifications";
import { computed, useSlots } from "vue";

const props = defineProps({
    backColor: {
        type: String,
        default: "",
    },
    canSkipGenerator: {
        type: Boolean,
        default: true,
    },
    description: {
        type: String,
        required: true,
    },
    title: {
        type: String,
        required: true,
    },
    bigSvgSize: {
        type: Boolean,
        default: false,
    },
    video: {
        type: Object,
        default: () => ({
            id: "",
            title: "",
            subtitle: "",
            videoLink: "",
            imageLink: "",
            actionLink: "",
        }),
    },
});
defineEmits(["close"]);

const slots = useSlots();
const hasPreviewSlot = computed(() => Boolean(slots.preview));

function goToSeeMore() {
    const { actionLink } = props.video;
    window.open(actionLink, "_blank");
}
</script>

<template>
    <Teleport to="body">
        <div
            class="fixed z-20 top-16 bottom-0 left-0 right-0 w-full grid grid-cols-2 ct-bg-1--alt text-left"
        >
            <div
                v-if="video.show"
                class="absolute right-2 top-2 space-y-6 items-start hidden lg:block w-[40%] z-10"
            >
                <NotificationWithVideo
                    :title="video.title"
                    :subtitle="video.subtitle"
                    :ctaLabel="$t('shared.creator.help_video.watch')"
                    :altLabel="$t('shared.creator.help_video.read_more')"
                    :altCallback="goToSeeMore"
                    :wistiaId="video.id"
                    ctaPlaysVideo
                    dontApplyAspectVideo
                    @close="video.closeVideo()"
                />
            </div>
            <div class="overflow-y-auto no-scrollbar px-12 py-16 lg:col-span-1 col-span-full">
                <div class="flex-shrink-0 flex items-start space-x-4 mb-9">
                    <div class="flex-grow space-y-2">
                        <p class="ct-text-1 text-3xl font-medium">{{ title }}</p>
                        <p class="ct-text-2 text-base">{{ description }}</p>
                    </div>
                </div>
                <div class="pl-4">
                    <slot></slot>
                </div>
            </div>
            <div
                v-if="hasPreviewSlot"
                class="@container hidden ct-bg-3 lg:flex w-full items-center min-w-0 min-h-0 justify-center no-scrollbar col-span-1 overflow-hidden"
                :style="{ 'background-color': backColor }"
            >
                <section
                    v-if="bigSvgSize"
                    class="@lg:scale-50 @2xl:scale-[0.6] @3xl:scale-[0.8] @[59rem]:scale-100"
                >
                    <slot name="preview"></slot>
                </section>
                <section v-else class="@lg:scale-[0.8] @3xl:scale-[0.9] @[59rem]:scale-100">
                    <slot name="preview"></slot>
                </section>
            </div>
            <div class="absolute bottom-6 right-6 hidden lg:block">
                <ButtonSecondaryLg @click="$emit('close')" :disabled="!canSkipGenerator">
                    <SkipForwardIcon />
                    <span>{{ $t("shared.creator.skip") }}</span>
                </ButtonSecondaryLg>
            </div>
        </div>
    </Teleport>
</template>
